import React, { useState, useCallback } from 'react';
import { Modal, Radio, Row } from "antd";
import { noop } from 'lodash';
import {useTranslation} from 'react-i18next'
import DoubleArrowImage from 'src/assets/img/double-arrow.svg';
import PeopleItem from 'src/components/people-item';
import FormItem from 'src/components/form-item';
import { FitPeopleLabel } from 'src/components/fit-people-label';
import { filteredAndRejected } from 'src/utils/helpers';
import './style.scss';
import Exclamation from "src/assets/img/icon_exclamation_circle.svg";
import NeedItem from 'src/components/need-item';
import MatchRequisitionModal from 'src/pages/people/matchRequisitionModal/MatchRequisitionModal';

/**
 * Compares two objects based on the `collaborator_deallocation.login` property.
 *
 * @param {{ collaborator_deallocation: { login: string } }} a - The first object to compare.
 * @param {{ collaborator_deallocation: { login: string } }} b - The second object to compare.
 * @returns {number} The comparison result. Returns a negative value if `a` should be sorted before `b`, a positive value if `a` should be sorted after `b`, or 0 if they are equal.
 */
const compare = (a, b) => a.collaborator_deallocation.login.localeCompare(b.collaborator_deallocation.login);

/**
 * Retrieves deallocations with suggestions based on the given need and deallocations.
 *
 * @param {Object} need - The need object.
 * @param {Array<Object>} deallocations - The array of deallocation objects.
 * @returns {Array<Object>} The filtered and sorted array of deallocations with suggestions.
 */
const getDeallocationsWithSuggestions = (need, deallocations) => {
    const suggestionDeallocations = deallocations.map((dealloc) => {
        const suggestion = dealloc.deallocation_suggestion.find(suggestion => 
            suggestion.requisitionCode === need.requisitionCode);
        if (suggestion) {
            return {
                ...dealloc,
                suggestion: suggestion,
            }
        };

        return null;
    });
    return suggestionDeallocations.filter(dealloc => dealloc !== null).sort(compare);
}

/**
 * Retrieves the Fit ID associated with the given deallocation ID from the provided need.
 *
 * @param {Object} need - The need object.
 * @param {number} deallocationId - The ID of the deallocation.
 * @returns {number|undefined} The Fit ID associated with the deallocation, or undefined if not found.
 */
const getFitIdByDeallocation = (need, deallocationId) => {
    const deallocation = need.deallocations.find(dealloc => dealloc.id === deallocationId);
    if (!!deallocation) return deallocation.Fits.id;
};

/**
 * A Need component to show match modal where user can perform 'match' action on an existing fit.
 * 
 * @param {Object} props 
 * @param {Boolean} props.loading - Indicates whether loading is true or false.
 * @param {Object} props.need - Need object.
 * @param {Array<Object>} props.deallocations - Array with Deallocation objects.
 * @param {Function} props.onSave - Function to be called upon saving match.
 * @param {Function} props.onCancel - Function to be called upon cancelling action.
 * @returns {JSX.Element} The MatchModal component.
 * @example
 *  <ConfirmMatchModal
        loading={true}
        need={
            {
                prop1: '', 
                prop2: '', 
                // ...
            }
        }
        deallocations={
            [
                {prop1: '', prop2: ''}, 
                {prop1: '', prop2: ''}, 
                // ...
            ]
        }
        onSave={saveFn}
        onClose={closeFn}
    />
 */
function MatchModal({ loading, need, deallocations, onSave, onClose }) {
    
    const needId = need.id;
    const deallocationsIds = need.deallocations.length > 0 ?
        need.deallocations.map(dealloc => dealloc.id) :
        null;
    const optMatchDeallocations = getDeallocationsWithSuggestions(need, deallocations);
    /**
     * Retrieves the selected deallocation ID based on the provided criteria.
    *
    * @returns {number|undefined} The selected deallocation ID, or undefined if not found.
    */
    const getSelectedDeallocationId = () => {
        for (let dealloc of optMatchDeallocations.map(d => d)) {
            if (deallocationsIds.includes(dealloc.id)) return dealloc.id;
        }
        return need.deallocations.sort(compare)[0]?.id;
    }
    const [ selectedDeallocation, setSelectedDeallocation ] = useState(getSelectedDeallocationId());
    const [ fitId, setFitId ] = useState(getFitIdByDeallocation(need, selectedDeallocation));

    const { t } = useTranslation();
    const [openRequisitionModal, setOpenRequisitionModal] = useState(false);

    /**
     * Returns ordered deallocations to appear in Match Modal List. It includes, in this order:
     * - Fits with Opt Match suggestions.
     * - Fits without Opt Match suggestions.
     */
    const orderedResult = (() => {
        const [fitsPeople] = filteredAndRejected(deallocations, item => deallocationsIds.includes(item.id));

        // fits with opt match
        const optMatchFitDeallocations = getDeallocationsWithSuggestions(need, fitsPeople);
        optMatchFitDeallocations.sort(compare);

        // fits without opt match
        const otherFits = fitsPeople.filter(filterDealloc => !optMatchDeallocations.find(dealloc => dealloc.id === filterDealloc.id));
        otherFits.sort(compare);

        return [...optMatchFitDeallocations, ...otherFits];
    })();

    /**
     * Set new fitId upon radio selection change.
     */
    const onChangeNeed = useCallback(
        (deallocationId) => {
            const fitId = getFitIdByDeallocation(need, deallocationId);
            setFitId(fitId);
        },
        [setFitId, need]
    );

    /**
     * Returns rendered Radio options with deallocations.
     */
    const deallocationOptions = orderedResult
        .map(deallocation => {
            let labelType = 'fit';
            if (deallocation.suggestion) labelType = 'fit-opt-match';
            return (
                <React.Fragment key={deallocation.id}>
                    <FitPeopleLabel container="match" type={labelType} people={'people'}>
                        <div className={`fit-people ${labelType}`}>
                            <Radio value={deallocation.id} onChange={(e) => onChangeNeed(deallocation.id)}
                                className={`fit-radio`}
                            >
                                <PeopleItem people={deallocation} className={'remove-border'} />
                            </Radio>
                        </div>
                    </FitPeopleLabel>
                </React.Fragment>
            );
        });

    /**
     * Call onSave method from parent component upon clicking 'Ok' button.
     */
    const onOk = useCallback(() => {
        setOpenRequisitionModal(true)
    }, [setOpenRequisitionModal]);

    /**
     * Call onClose method from parent component upon clicking 'Cancel' button or minimizing Modal screen.
     */
    const onCancel = useCallback(() => {
        if (loading) {
            return;
        }

        onClose();
    }, [ loading, onClose ]);

    return (
        <>
            <MatchRequisitionModal 
                reqTitle={need.title}
                visible={openRequisitionModal}
                reqCode={need.requisitionCode}
                setVisible={setOpenRequisitionModal} 
                login={need.deallocations.find(dealloc => dealloc.id === selectedDeallocation).collaborator_deallocation.login}
                onConfirmMatch={(startDate, allocationType) => onSave(fitId, startDate, allocationType)}
            />
            <Modal
                title={t('decideMatch')}
                width={1000}
                className="opportunity-match"
                visible={true}
                confirmLoading={loading}
                onCancel={onCancel}
                onOk={onOk}
                okButtonProps={{ className : "cit-btn primary-type", disabled: !needId }}
                cancelButtonProps={{ className : "cit-btn link-type cancel-button"}}
                cancelText={t('back')}
                okText={t('confirmMatch')}
                aria-modal='true'>
                <div className='opportunity-match__subtitle'>
                    <Row align="top">
                        <div className='opportunity-match__subtitle_gutter' tabIndex={0} role='note'>
                            {t('confirmMatchSubtitleOpportunity')}
                        </div>
                    </Row>
                </div>
                <div className="opportunity-match__container">
                    <FormItem label={t('needsTitle')}>
                        <div className='opportunity-match__container_opportunity'>
                            <div className="option-list">
                                <NeedItem need={need}  className={'is-need-page'} />
                            </div>
                        </div>
                    </FormItem>
                    <div className="opportunity-match__container_arrow">
                        <img src={DoubleArrowImage} alt="match arrow" className="match-arrow-image" />
                    </div>
                    <div className="option-list">
                        <Radio.Group value={selectedDeallocation} onChange={e => setSelectedDeallocation(e.target.value)}>
                            {deallocationOptions}
                        </Radio.Group>
                    </div>
                </div>

                <div className='opportunity-match__alert' tabIndex={0} role="alert" aria-describedby='alert-container'>
                    <img src={Exclamation} alt="exclamation" />
                    <span>{t('decisionRegardingMatch')}</span>
                </div>
            </Modal>
        </>
    );
}

MatchModal.defaultProps = {
    need: {},
    deallocations: [],
    onClose: noop,
    onSave: noop,
    loading: false
};

export default MatchModal;
